
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { useSettings } from '@/contexts/SettingsContext';
import PageLayout from '@/components/PageLayout';
import AIAssistant from '@/components/AIAssistant';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CarIcon } from 'lucide-react';

const AIAssistantPage = () => {
  const { carId } = useParams<{ carId: string }>();
  const { cars } = useCar();
  const { currentCar, selectCar } = useCarSelectionContext();
  const { t } = useSettings();
  const navigate = useNavigate();
  
  const [selectedCarId, setSelectedCarId] = useState<string | null>(carId || null);
  
  useEffect(() => {
    if (carId) {
      selectCar(carId);
      setSelectedCarId(carId);
    }
  }, [carId, selectCar]);
  
  const handleCarChange = (newCarId: string) => {
    setSelectedCarId(newCarId);
    navigate(`/assistant/${newCarId}`);
  };
  
  const selectedCar = currentCar;
  
  return (
    <PageLayout title="Car Assistant" showBackButton onBack={() => navigate(-1)}>
      <div className="space-y-2">
        {cars.length > 0 ? (
          <>
            {selectedCar && (
              <Card className="mb-2">
                <CardContent className="p-3 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <CarIcon size={18} className="text-lime" /> {/* Changed color class */}
                    <div>
                      <p className="font-medium">{selectedCar.nickname || `${selectedCar.brand} ${selectedCar.model}`}</p>
                      <p className="text-sm text-gray-500">{selectedCar.year} • {selectedCar.licensePlate || 'No plate'}</p>
                    </div>
                  </div>
                  
                  {cars.length > 1 && (
                    <Select value={selectedCarId || ''} onValueChange={handleCarChange}>
                      <SelectTrigger className="w-36 h-8 text-xs">
                        <SelectValue placeholder="Switch car" />
                      </SelectTrigger>
                      <SelectContent>
                        {cars.map((car) => (
                          <SelectItem key={car.id} value={car.id}>
                            {car.nickname || `${car.brand} ${car.model}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </CardContent>
              </Card>
            )}
            
            <AIAssistant carId={selectedCarId || undefined} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CarIcon size={64} className="text-gray-300 mb-3" />
            <h3 className="text-xl font-semibold mb-2">No cars found</h3>
            <p className="text-gray-500 mb-4">Add a car to get started with the assistant</p>
            <button 
              onClick={() => navigate('/cars/add')}
              className="bg-lime text-primary-foreground px-4 py-2 rounded-md" /* Changed from text-black */
            >
              Add Your First Car
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AIAssistantPage;
